import React, { useCallback, useEffect, useState } from 'react';
import { contactApi } from '../services';
import documentsApi from '../services/documentsApi';
import googleApi from '../services/googleApi';
import { formatDateTime, timeAgo } from '../utils/time';
import Icon from './Icon';
import './MeetingsSection.css';

const STATUS_LABELS = { active: 'Connected', revoked: 'Disconnected', error: 'Needs reconnecting' };

/** Who has connected a Google account, whether it is syncing, and Disconnect for anyone who leaves sales. */
export const GoogleConnectionsTab = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => googleApi.connections().then(setData).catch(err => setError(err.message)), []);
  useEffect(() => {
    load();
  }, [load]);

  const connectShared = async () => {
    setBusy(true);
    setError(null);
    try {
      const { authorization_url: url } = await googleApi.connectShared();
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const disconnect = async (connection) => {
    if (!window.confirm(`Disconnect ${connection.google_email}? Sinatra stops reading it and cancels its access with Google.`)) return;
    setBusy(true);
    try {
      await googleApi.adminDisconnect(connection.id);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return error
      ? <p className="company-panel-error" role="alert"><Icon name="alert-triangle" /> {error}</p>
      : <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>;
  }

  const shared = data.connections.find(connection => connection.shared && connection.status === 'active');
  return (
    <div className="adm-tab">
      {error && <p className="company-panel-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {!data.configured && (
        <p className="placeholder-text">Not set up: {data.missing.join(', ')} missing from the server's settings. Nobody can connect until they are set.</p>
      )}
      {data.configured && !shared && (
        <div className="google-notice">
          <p>The shared account <strong>{data.shared_account}</strong> is on every client invite, so its calendar holds nearly every client meeting. Connect it once: sign in to Google as {data.shared_account} when asked.</p>
          <button type="button" className="rm-button rm-button--primary" onClick={connectShared} disabled={busy}>
            <Icon name="calendar" /> Connect {data.shared_account}
          </button>
        </div>
      )}
      {data.connections.length === 0 && <p className="placeholder-text">Nobody has connected a Google account yet.</p>}
      <ul className="adm-users">
        {data.connections.map(connection => (
          <li key={connection.id} className="adm-user">
            <div className="adm-user__main">
              <span className="adm-user__name">{connection.user_name || connection.google_email}{connection.shared && ' (shared)'}</span>
              <span className={`google-status${connection.status === 'error' ? ' google-status--error' : ''}`}>
                <span>{connection.google_email}</span>
                <span>{STATUS_LABELS[connection.status] || connection.status}</span>
                {connection.error_note && <span>{connection.error_note}</span>}
                {connection.status === 'active' && (
                  <span className="meet__meta">
                    {connection.calendar_synced_at
                      ? <>synced <span title={formatDateTime(connection.calendar_synced_at)}>{timeAgo(connection.calendar_synced_at)}</span></>
                      : 'not synced yet'}
                    {!connection.shared && !connection.acknowledged_at && ' · waiting for them to acknowledge'}
                    {!connection.calendar && ' · no calendar access'}
                  </span>
                )}
              </span>
            </div>
            <div className="adm-user__side">
              {connection.status !== 'revoked' && (
                <button type="button" className="rm-button rm-button--quiet" onClick={() => disconnect(connection)} disabled={busy}>
                  Disconnect
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const defaultName = (candidate) => {
  if (candidate.kind !== 'domain') return '';
  const labels = candidate.key.split('.');
  const word = labels.length > 1 ? labels[labels.length - 2] : labels[0];
  return word.replace(/-/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
};

const Candidate = ({ candidate, busy, onAdd, onDismiss }) => {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState(defaultName(candidate));
  const [emails, setEmails] = useState([]);
  const label = (attendee) => attendee.name || attendee.email;

  return (
    <li className="adm-event">
      <div className="adm-event__line">
        <span className="adm-event__who">{candidate.key}</span>
        <span className="adm-event__when">
          {candidate.meetings} meeting{candidate.meetings === 1 ? '' : 's'} · latest {timeAgo(candidate.latest_at)}
        </span>
      </div>
      <div className="adm-finding__meta">
        {candidate.latest_title} · with {candidate.tileward.join(', ') || 'Tileward'}
        {' · '}{candidate.attendees.map(label).join(', ')}
      </div>
      {adding ? (
        <div className="cand__edit">
          <input className="rm-input" type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="Company name" aria-label="Company name" disabled={busy} />
          {candidate.attendees.map(attendee => (
            <label key={attendee.email} className="rm-picker__option">
              <input type="checkbox" checked={emails.includes(attendee.email)} disabled={busy}
                onChange={() => setEmails(current => (current.includes(attendee.email)
                  ? current.filter(item => item !== attendee.email) : [...current, attendee.email]))} />
              <span className="rm-picker__label">Add {label(attendee)} as a contact <span className="rm-picker__title">· {attendee.email}</span></span>
            </label>
          ))}
          <div className="adm-finding__actions">
            <button type="button" className="rm-button rm-button--primary" disabled={busy || !name.trim()}
              onClick={() => onAdd(candidate, name, emails)}><Icon name="check" /> Add company</button>
            <button type="button" className="rm-button rm-button--quiet" disabled={busy} onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="adm-finding__actions">
          <button type="button" className="rm-button rm-button--primary" disabled={busy} onClick={() => setAdding(true)}>
            <Icon name="plus" /> Add as company
          </button>
          <button type="button" className="rm-button rm-button--quiet" disabled={busy} onClick={() => onDismiss(candidate)}>Not a client</button>
        </div>
      )}
    </li>
  );
};

/** Outside people met whose company isn't on file: add each as a company, or rule it out for good. */
export const CandidateClientsTab = () => {
  const [candidates, setCandidates] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => googleApi.candidates()
    .then(data => setCandidates(data.candidates)).catch(err => setError(err.message)), []);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (operation, message) => {
    setBusy(true);
    setError(null);
    try {
      await operation();
      setNote(message);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="adm-tab">
      <p className="adm-add__hint">Meetings with people outside Tileward whose company isn't on file. Nothing is added until you add it.</p>
      {error && <p className="company-panel-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {note && <p className="google-status" role="status"><Icon name="check" /> {note}</p>}
      {!candidates && !error && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}
      {candidates && candidates.length === 0 && <p className="placeholder-text">No candidate clients right now.</p>}
      {candidates && candidates.length > 0 && (
        <ul className="adm-events">
          {candidates.map(candidate => (
            <Candidate
              key={candidate.key}
              candidate={candidate}
              busy={busy}
              onAdd={(item, name, emails) => act(
                () => googleApi.addCandidate(item.key, { name, contact_emails: emails }), `Added ${name}`)}
              onDismiss={(item) => act(() => googleApi.dismissCandidate(item.key), `Ruled out ${item.key}`)}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

/** Finds a company by name for a folder that matched none: type, then pick. */
const CompanyPicker = ({ folderName, busy, onPick }) => {
  const [text, setText] = useState('');
  const [found, setFound] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (text.trim().length < 2) {
      setFound([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      contactApi.searchCompanies({ q: text.trim(), limit: 6 })
        .then(({ companies }) => { if (!cancelled) { setFound(companies); setError(null); } })
        .catch(err => { if (!cancelled) setError(err.message); });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [text]);

  return (
    <div className="lf__picker">
      <input className="rm-input" type="search" value={text} onChange={e => setText(e.target.value)}
        placeholder="Find the company" aria-label={`Company for the folder ${folderName}`} disabled={busy} />
      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {found.length > 0 && (
        <ul className="lf__results">
          {found.map(company => (
            <li key={company.id}>
              <button type="button" className="rm-link" disabled={busy} onClick={() => onPick(company)}>{company.name}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * The legal shared drive's folders. A folder whose name matches one company is proposed for it; a person confirms
 * it here once, and it is saved as the company's folder (renaming either side changes nothing). A folder that
 * matches nothing waits under "Folders with no company" until someone picks its company.
 */
export const LegalFoldersTab = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => documentsApi.legalFolders().then(setData).catch(err => setError(err.message)), []);
  useEffect(() => {
    load();
  }, [load]);

  const link = async (folder, company, message) => {
    setBusy(true);
    setError(null);
    try {
      await documentsApi.linkLegalFolder(folder.folder_id, company ? company.id : null);
      setNote(message);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return error
      ? <p className="company-panel-error" role="alert"><Icon name="alert-triangle" /> {error}</p>
      : <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>;
  }

  return (
    <div className="adm-tab">
      <p className="adm-add__hint">
        Each company has a folder in the legal shared drive. Confirm which is whose once; documents that appear in a
        folder are suggested on that company's panel.
      </p>
      {error && <p className="company-panel-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {note && <p className="google-status" role="status"><Icon name="check" /> {note}</p>}
      {!data.configured && <p className="placeholder-text">GOOGLE_LEGAL_DRIVE_ID isn't set on the server, so the drive isn't being scanned.</p>}
      {data.configured && !data.linked.length && !data.proposed.length && !data.no_company.length && (
        <p className="placeholder-text">No folders seen yet; the first scan lists them within half an hour of connecting the shared account.</p>
      )}

      {data.proposed.length > 0 && (
        <>
          <h4>Proposed matches</h4>
          <ul className="adm-events">
            {data.proposed.map(folder => (
              <li key={folder.folder_id} className="adm-event">
                <div className="adm-event__line">
                  <span className="adm-event__who">{folder.name}</span>
                  <span className="adm-event__when">looks like {folder.proposed.name}</span>
                </div>
                <div className="adm-finding__actions">
                  <button type="button" className="rm-button rm-button--primary" disabled={busy}
                    onClick={() => link(folder, folder.proposed, `Linked ${folder.name} to ${folder.proposed.name}`)}>
                    <Icon name="check" /> Link
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {data.no_company.length > 0 && (
        <>
          <h4>Folders with no company</h4>
          <ul className="adm-events">
            {data.no_company.map(folder => (
              <li key={folder.folder_id} className="adm-event">
                <div className="adm-event__line"><span className="adm-event__who">{folder.name}</span></div>
                <CompanyPicker folderName={folder.name} busy={busy}
                  onPick={(company) => link(folder, company, `Linked ${folder.name} to ${company.name}`)} />
              </li>
            ))}
          </ul>
        </>
      )}

      {data.linked.length > 0 && (
        <>
          <h4>Linked</h4>
          <ul className="adm-events">
            {data.linked.map(folder => (
              <li key={folder.folder_id} className="adm-event">
                <div className="adm-event__line">
                  <span className="adm-event__who">{folder.name}</span>
                  <span className="adm-event__when">{folder.company.name}</span>
                </div>
                <div className="adm-finding__actions">
                  <button type="button" className="rm-button rm-button--quiet" disabled={busy}
                    onClick={() => link(folder, null, `Unlinked ${folder.name}`)}>Unlink</button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
